/* components/patterns/InvoicePayModal.jsx — безналичная оплата подписки по счёту (банк Точка).
   Только бизнес-тарифы, за фиче-флагом (кнопка-точка входа гейтит user.tochka_invoicing_enabled).

   Форма: выбор платного бизнес-тарифа (Select) → сумма → реквизиты плательщика
   (тип ИП/ООО через Segmented; ИНН; КПП — только и обязателен для ООО; юр.название)
   → акцепт оферты (чекбокс со ссылкой на /legal/offer). Кнопка «Выставить счёт»
   заблокирована, пока оферта не принята или форма невалидна.

   Валидация дублируется на фронте (lib/payerRequisites.js: ИНН ООО — 10 цифр, ИП — 12,
   контрольная цифра; КПП 9 цифр), перепутанный тип и неверная контрольная видны у поля
   сразу. Окончательное слово — за 400-ответом бэка (unwrap → throw {detail,code}): ошибку
   поля (в т.ч. отказ банка по ИНН, `bank_rejected_inn`) кладём под поле, прочее — в toast.
   Успех → onCreated(invoice_id) (родитель ведёт на экран счёта).

   Контракт: POST /api/subscription/invoice/checkout/
     body {plan_slug, requisites:{legal_type,inn,kpp,legal_name}, offer_accepted:true}. */

import React from 'react';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, Segmented, TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Select } from '../../components/ui/Select.jsx';
import { subscriptionReal } from '../../lib/adapters/subscription.js';
import { fmt } from '../../lib/format.js';
import { innError, innLiveError, legalTypeForSaved, onlyDigits } from '../../lib/payerRequisites.js';
// Оферту показываем ВНУТРИ модалки (переиспользуем рендер правового документа
// демо-стенда), чтобы не уводить со страницы и не терять заполненную форму.
import { DemoLegalDoc } from '../../screens/demo/DemoLegalDoc.jsx';

  const { useState, useMemo, useEffect } = React;

  const LEGAL_TYPES = [
    { value: 'ip', label: 'ИП' },
    { value: 'company', label: 'ООО' },
  ];

  function InvoicePayModal({ open, onClose, plans = [], wsId, onCreated, fixedPlanSlug = null,
                             submitInvoice = null, planLabel = '', amountRub = null,
                             canPrefill = true }) {
    const toast = useToast();

    const [planSlug, setPlanSlug] = useState('');
    const [legalType, setLegalType] = useState('ip');
    const [inn, setInn] = useState('');
    const [kpp, setKpp] = useState('');
    const [legalName, setLegalName] = useState('');
    const [offerAccepted, setOfferAccepted] = useState(false);
    const [errors, setErrors] = useState({});
    const [innFocused, setInnFocused] = useState(false);   // человек ещё набирает ИНН
    const [busy, setBusy] = useState(false);
    const [showOffer, setShowOffer] = useState(false);   // оферта открыта внутри модалки

    // Сброс формы при каждом открытии (модалка живёт между открытиями) + префилл
    // сохранёнными реквизитами пространства (повторная оплата — без перенабора).
    useEffect(() => {
      if (!open) return;
      // Тариф либо зафиксирован родителем (пришли из выбора способа оплаты), либо
      // выбирается в модалке (одиночный список → первый).
      setPlanSlug(fixedPlanSlug || (plans.length === 1 ? plans[0].slug : ''));
      setLegalType('ip');
      setInn(''); setKpp(''); setLegalName('');
      setOfferAccepted(false);
      setErrors({});
      setBusy(false);
      setShowOffer(false);
      let cancelled = false;
      // Счёт по ссылке вправе оплатить посторонний (бухгалтер, учредитель) — у
      // него нет ни сессии, ни оснований видеть ИНН и юрназвание клиента.
      // `canPrefill=false` выключает подстановку целиком, а не полагается на
      // 403 от эндпоинта: молчаливый отказ выглядел бы как «префилл не
      // сработал», и однажды кто-нибудь «починил» бы его обратно.
      if (!canPrefill) return undefined;
      subscriptionReal.getPayerRequisites(wsId).then((r) => {
        if (cancelled || !r || !r.inn) return;
        // Тип — по ИНН, если сохранённый с ним спорит (см. legalTypeForSaved).
        const savedType = legalTypeForSaved(r.legal_type, r.inn);
        if (savedType) setLegalType(savedType);
        setInn(r.inn || '');
        setKpp(r.kpp || '');
        setLegalName(r.legal_name || '');
      }).catch(() => {});   // префилл — best-effort, форма работает и без него
      return () => { cancelled = true; };
    }, [open, fixedPlanSlug, wsId, canPrefill]);

    const isCompany = legalType === 'company';

    const planOptions = useMemo(
      () => plans.map((p) => ({ value: p.slug, label: p.name, hint: fmt.money(p.price_rub) })),
      [plans]
    );
    const selectedPlan = plans.find((p) => p.slug === planSlug) || null;

    // Тариф может прийти ИЗВНЕ, без списка `plans`: так работает оплата по
    // именной ссылке — что и почём, решил менеджер при выпуске, клиент это не
    // выбирает. Тогда показываем его строкой и не требуем выбора в валидации.
    const planFromOutside = !!submitInvoice;
    const fixedPlanView = selectedPlan
      ? { name: selectedPlan.name, price: selectedPlan.price_rub }
      : (planLabel ? { name: planLabel, price: amountRub } : null);

    // Мягкая клиентская валидация (зеркалит коды бэка, но не заменяет их).
    const validate = () => {
      const e = {};
      if (!planSlug && !planFromOutside) e.plan = 'Выберите тариф.';
      const innErr = innError(legalType, inn);   // ИП — 12 цифр, ООО — 10, контрольная цифра
      if (innErr) e.inn = innErr;
      if (isCompany && onlyDigits(kpp).length !== 9) e.kpp = 'КПП — 9 цифр.';
      if (!legalName.trim()) e.legalName = 'Укажите юридическое название.';
      return e;
    };

    const clientErrors = validate();
    const formValid = Object.keys(clientErrors).length === 0;
    const canSubmit = offerAccepted && formValid && !busy;
    // Ошибку ИНН показываем сразу: кнопка на невалидной форме заблокирована,
    // и без текста у поля человек не узнал бы, почему.
    const innShown = errors.inn || innLiveError(legalType, inn, { typing: innFocused });
    // Ответ сервера про ИНН устаревает, как только ИНН или тип поменяли.
    const clearInnError = () => setErrors((prev) => (prev.inn ? { ...prev, inn: undefined } : prev));

    // Ошибку бэка (400 code) кладём под нужное поле, если распознали, иначе — в toast.
    const CODE_FIELD = {
      no_plan_slug: 'plan', not_business_plan: 'plan', unknown_plan: 'plan', zero_amount: 'plan',
      bad_inn: 'inn', bank_rejected_inn: 'inn', kpp_required: 'kpp', legal_name_required: 'legalName',
    };

    const submit = async () => {
      const e = validate();
      setErrors(e);
      if (Object.keys(e).length || !offerAccepted) return;
      setBusy(true);
      try {
        const requisites = {
          legal_type: legalType,
          inn: onlyDigits(inn),
          kpp: isCompany ? onlyDigits(kpp) : '',
          legal_name: legalName.trim(),
        };
        // `submitInvoice` подменяет ТОЛЬКО сам вызов: форма реквизитов, валидация,
        // оферта и разбор ошибок общие. Второй потребитель — оплата по именной
        // ссылке менеджера: там счёт выставляет свой эндпоинт (`/pay/<token>/
        // invoice/`), который берёт тариф, пространство и сумму из ссылки, а не
        // из сессии. Копия этой формы неизбежно разъехалась бы с оригиналом —
        // и разъехалась бы в валидации ИНН, то есть в деньгах.
        const inv = submitInvoice
          ? await submitInvoice(requisites)
          : await subscriptionReal.startInvoiceCheckout(planSlug, requisites, true, wsId);
        if (inv && inv.invoice_id) {
          onCreated && onCreated(inv.invoice_id);
        } else {
          toast.show('Не удалось выставить счёт', 'danger');
        }
      } catch (err) {
        const field = err && err.code && CODE_FIELD[err.code];
        if (field) {
          setErrors((prev) => ({ ...prev, [field]: err.detail || err.message }));
        } else if (err && err.code === 'feature_disabled') {
          toast.show('Оплата по счёту недоступна для этого пространства', 'danger');
        } else if (err && err.code === 'tochka_error') {
          toast.show('Банк временно недоступен. Попробуйте позже.', 'danger');
        } else {
          toast.show((err && (err.detail || err.message)) || 'Не удалось выставить счёт', 'danger');
        }
        setBusy(false);
      }
    };

    return (
      <Modal open={open}
        onClose={busy ? undefined : (showOffer ? () => setShowOffer(false) : onClose)}
        title={showOffer ? 'Пользовательское соглашение' : 'Оплата по счёту (для юрлиц)'}
        size={showOffer ? 'lg' : 'md'} labelledBy="invoice-pay-title"
        footerLeft={!showOffer && fixedPlanView && fixedPlanView.price != null
          ? <span className="t-body-sm text-dim">К оплате: <span className="num">{fmt.money(fixedPlanView.price)}</span></span>
          : null}
        footer={showOffer
          ? <Button variant="ghost" iconLeft="arrow-left" onClick={() => setShowOffer(false)}>Назад к счёту</Button>
          : (
            <>
              <Button variant="ghost" disabled={busy} onClick={onClose}>Отмена</Button>
              <Button variant="primary" iconLeft="file-text" loading={busy} disabled={!canSubmit} onClick={submit}>
                Выставить счёт
              </Button>
            </>
          )}>
        {showOffer ? (
          <DemoLegalDoc kind="offer" />
        ) : (
        <div className="stack stack-16">
          <div className="callout callout--info">
            <Icon name="info" size={16} />
            <div className="grow t-body-sm text-dim">
              Мы сформируем счёт на оплату для вашего юрлица. После оплаты по банковским
              реквизитам тариф оформится автоматически.
            </div>
          </div>

          {(fixedPlanSlug || planFromOutside) ? (
            fixedPlanView && (
              <Field label="Тариф">
                <div className="inv-plan-fixed">
                  <span className="t-label">{fixedPlanView.name}</span>
                  {fixedPlanView.price != null && (
                    <span className="t-body-sm text-mute">за {fmt.money(fixedPlanView.price)}</span>
                  )}
                </div>
              </Field>
            )
          ) : (
            <Field label="Тариф" htmlFor="inv-plan" error={errors.plan}>
              <Select value={planSlug} onChange={setPlanSlug} options={planOptions} searchable={false}
                placeholder="Выберите бизнес-тариф" invalid={!!errors.plan} ariaLabel="Бизнес-тариф" />
            </Field>
          )}

          <Field label="Тип плательщика">
            <Segmented value={legalType} options={LEGAL_TYPES} ariaLabel="Тип плательщика"
              onChange={(v) => { setLegalType(v); clearInnError(); }} />
          </Field>

          <Field label="ИНН" htmlFor="inv-inn" error={innShown}
            hint={!innShown ? (isCompany ? '10 цифр' : '12 цифр') : undefined}>
            <TextInput id="inv-inn" value={inn} inputMode="numeric" autoComplete="off"
              invalid={innShown ? true : undefined}
              placeholder={isCompany ? '7707083893' : '000000000000'}
              onFocus={() => setInnFocused(true)} onBlur={() => setInnFocused(false)}
              onChange={(e) => { setInn(onlyDigits(e.target.value).slice(0, 12)); clearInnError(); }} />
          </Field>

          {isCompany && (
            <Field label="КПП" htmlFor="inv-kpp" error={errors.kpp} hint={!errors.kpp ? '9 цифр' : undefined}>
              <TextInput id="inv-kpp" value={kpp} inputMode="numeric" autoComplete="off"
                invalid={errors.kpp ? true : undefined} placeholder="770701001"
                onChange={(e) => setKpp(onlyDigits(e.target.value).slice(0, 9))} />
            </Field>
          )}

          <Field label={isCompany ? 'Название организации' : 'Наименование ИП'} htmlFor="inv-name" error={errors.legalName}>
            <TextInput id="inv-name" value={legalName} autoComplete="organization"
              invalid={errors.legalName ? true : undefined}
              placeholder={isCompany ? 'ООО «Ромашка»' : 'ИП Иванов Иван Иванович'}
              onChange={(e) => setLegalName(e.target.value)} />
          </Field>

          {/* Акцепт оферты — чекбокс + ссылка, открывающая оферту ВНУТРИ модалки
              (без ухода со страницы, чтобы не терять заполненную форму). */}
          <div className="auth-form__agree">
            <input id="inv-offer" type="checkbox" className="checkbox__input" checked={offerAccepted}
              aria-label="Я принимаю оферту" onChange={(e) => setOfferAccepted(e.target.checked)} />
            <label htmlFor="inv-offer" className="checkbox__box" aria-hidden="true">
              <Icon name="check" size={12} />
            </label>
            <span className="auth-form__agree-text">
              <label htmlFor="inv-offer" className="auth-form__agree-lead">Я принимаю</label>{' '}
              <button type="button" className="demo-linkbtn" onClick={() => setShowOffer(true)}>оферту</button>
            </span>
          </div>
        </div>
        )}
      </Modal>
    );
  }

export { InvoicePayModal };
